import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  LinearProgress,
  Typography
} from '@mui/material';
import { Timer as TimerIcon } from '@mui/icons-material';

const InactivityWarning = ({ onExtend }) => {
  const [open, setOpen] = useState(false);
  const [remainingMinutes, setRemainingMinutes] = useState(5);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const handleInactivityWarning = (event) => {
      setRemainingMinutes(event.detail.remainingMinutes);
      setOpen(true);
      setProgress(100);
      
      // Iniciar countdown visual
      const startTime = Date.now();
      const duration = event.detail.remainingMinutes * 60 * 1000;
      
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, duration - elapsed);
        const progressValue = (remaining / duration) * 100;
        
        setProgress(progressValue);
        
        // Actualizar minutos restantes
        const minutesLeft = Math.ceil(remaining / 60000);
        if (minutesLeft !== remainingMinutes && minutesLeft > 0) {
          setRemainingMinutes(minutesLeft);
        }
        
        if (remaining > 0) {
          requestAnimationFrame(updateProgress);
        }
      };
      
      requestAnimationFrame(updateProgress);
    };

    window.addEventListener('inactivityWarning', handleInactivityWarning);
    
    return () => {
      window.removeEventListener('inactivityWarning', handleInactivityWarning);
    };
  }, [remainingMinutes]);

  const handleExtend = () => {
    setOpen(false);
    if (onExtend) {
      onExtend();
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ 
        top: { xs: 24, sm: 80 },
        maxWidth: 500
      }}
    >
      <Alert
        severity="warning"
        variant="filled"
        sx={{
          width: '100%',
          boxShadow: 3,
          '& .MuiAlert-icon': {
            fontSize: 28
          },
          '& .MuiAlert-message': {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }
        }}
        icon={<TimerIcon />}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleExtend}
              variant="outlined"
              sx={{ 
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Continuar
            </Button>
          </Box>
        }
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Your session is about to expire
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5 }}>
            {remainingMinutes === 1 
              ? 'Your session will expire in less than 1 minute due to inactivity.'
              : `Your session will expire in ${remainingMinutes} minutes due to inactivity.`
            }
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={progress}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white',
                borderRadius: 2
              }
            }}
          />
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default InactivityWarning;
